import { useEffect, useState } from 'react';

import { useTheme } from '@/providers/ThemeProvider/ThemeProvider';

export const THEME_MODE = {
  DARK: 'dark',
  LIGHT: 'light'
} as const;

export type ThemeCheckType = 'dark' | 'light';

const getSystemPrefersDark = (): boolean => {
  if (typeof window === 'undefined') {
    return false; // Default to light theme on server
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const useThemeMode = (check: ThemeCheckType): boolean => {
  const { theme } = useTheme();
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (theme === THEME_MODE.DARK) return true;
    if (theme === THEME_MODE.LIGHT) return false;
    return getSystemPrefersDark();
  });

  useEffect(() => {
    if (theme === THEME_MODE.DARK) {
      setIsDark(true);
      return;
    }

    if (theme === THEME_MODE.LIGHT) {
      setIsDark(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setIsDark(media.matches);

    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [theme]);

  return check === 'dark' ? isDark : !isDark;
};
